import { useEffect, useState } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import { meApi, kycApi } from '../../api/endpoints'
import { ApiError, type UserClaims } from '../../api/types'
import { useIdentity, useIdentityProfile } from '../../lib/identity'
import { InventoryConnectCard } from '../supplier/InventoryConnectCard'
import { VerificationDocs } from '../profile/VerificationDocs'

/* ─────────────── Shared premium graphics ───────────────
 * A frosted "vault" motif — a faceted diamond drawn in navy + gold, floated
 * behind/beside the headline. Pure SVG (no asset request), reduced-motion aware
 * (the caller decides whether the shimmer runs). Tones are inline rgba so it
 * carries in BOTH light and dark without touching index.css. */
function DiamondMotif({ animate }: { animate: boolean }) {
  return (
    <div className="pointer-events-none relative h-full w-full" aria-hidden>
      {/* soft radial glow bed */}
      <div
        className="absolute inset-0 rounded-[28px]"
        style={{
          background:
            'radial-gradient(120% 120% at 70% 25%, rgba(201,169,74,0.20) 0%, rgba(201,169,74,0.06) 38%, rgba(14,42,79,0.05) 70%, transparent 100%)',
        }}
      />
      <motion.svg
        viewBox="0 0 200 200"
        className="absolute left-1/2 top-1/2 h-[78%] w-[78%] -translate-x-1/2 -translate-y-1/2"
        initial={animate ? { opacity: 0, scale: 0.9, rotate: -6 } : false}
        animate={animate ? { opacity: 1, scale: 1, rotate: 0 } : { opacity: 1 }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      >
        <defs>
          <linearGradient id="dm-gold" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#e4cd86" />
            <stop offset="55%" stopColor="#c9a94a" />
            <stop offset="100%" stopColor="#a8842a" />
          </linearGradient>
          <linearGradient id="dm-navy" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="rgba(35,69,111,0.55)" />
            <stop offset="100%" stopColor="rgba(10,31,60,0.35)" />
          </linearGradient>
        </defs>
        {/* crown + pavilion facets */}
        <g stroke="rgba(201,169,74,0.55)" strokeWidth="1.1" strokeLinejoin="round">
          <polygon points="60,66 140,66 100,30" fill="url(#dm-gold)" opacity="0.92" />
          <polygon points="60,66 40,90 100,30" fill="url(#dm-navy)" opacity="0.8" />
          <polygon points="140,66 160,90 100,30" fill="url(#dm-navy)" opacity="0.8" />
          <polygon points="40,90 160,90 100,66" fill="url(#dm-gold)" opacity="0.42" />
          <polygon points="60,66 140,66 100,66" fill="url(#dm-gold)" opacity="0.6" />
          <polygon points="40,90 100,66 100,170" fill="url(#dm-navy)" opacity="0.72" />
          <polygon points="160,90 100,66 100,170" fill="url(#dm-navy)" opacity="0.58" />
          <polygon points="40,90 160,90 100,170" fill="url(#dm-gold)" opacity="0.3" />
        </g>
        {/* travelling glint */}
        {animate && (
          <motion.line
            x1="52" y1="52" x2="148" y2="148"
            stroke="rgba(255,255,255,0.85)" strokeWidth="2.4" strokeLinecap="round"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: [0, 1, 1], opacity: [0, 0.9, 0] }}
            transition={{ duration: 2.6, times: [0, 0.4, 1], repeat: Infinity, repeatDelay: 3.2, ease: 'easeInOut' }}
          />
        )}
      </motion.svg>
    </div>
  )
}

/**
 * BecomeSupplierPanel — the LIGHTWEIGHT buyer→seller path.
 *
 * A signed-in buyer who wants to list stock already has an account, so they
 * must NOT be sent back through the whole signup wizard (that route dead-ends:
 * /signup redirects an already-signed-in user straight to /search). Instead we
 * do the one thing the wizard's supplier steps actually do that a buyer hasn't:
 *   1. Declare the account a SUPPLIER account — this is the ONLY gate on the
 *      stock-connect API (`requireSupplierContext` accepts accountType
 *      'supplier'; the ROLE claim stays 'buyer' until admin promotion, which is
 *      fine — the KYC reviewer decides on accountType). We reuse the existing
 *      `POST /me/signup-profile` endpoint (merge-safe) with the name/company we
 *      already hold, so there's no re-typing the whole profile.
 *   2. Connect inventory — the SAME `InventoryConnectCard` (→ DataSourceSetup)
 *      a supplier gets on My Stock. Deferrable ("Save for later").
 *   3. Submit verification documents — `VerificationDocs`, the same seat-docs
 *      surface used on the profile.
 *
 * ── THE LIVE GATE ─────────────────────────────────────────────────────────
 * Connecting inventory is deferrable, but NOTHING GOES LIVE until the
 * verification documents are submitted AND our team approves them. That gate is
 * enforced server-side (a buyer's stock connection never reaches the catalog
 * before the account is KYC-approved), and this panel states it out loud with a
 * banner keyed off `kycApi.status()` — an unverified supplier account sees
 * "Submit your documents to start selling", never a false "you're live".
 *
 * Rendered inside /sell for a signed-in non-supplier. Suppliers never see it
 * (they're redirected to /sell/integrations); signed-out visitors get the
 * marketing pitch + the real signup wizard.
 */
export function BecomeSupplierPanel() {
  const identity = useIdentity()
  const { user, reload } = useIdentityProfile()

  // Supplier CONTEXT — matches the server's `requireSupplierContext`: role
  // supplier/admin, or accountType 'supplier' (an org that sells also counts
  // server-side, but a buyer flipping themselves here goes through accountType).
  const isSupplierContext =
    identity.role === 'supplier' ||
    identity.role === 'admin' ||
    user?.accountType === 'supplier'

  if (isSupplierContext) return <ConnectAndVerify />
  // Re-read /me so the freshly-set account='supplier' flips us into
  // ConnectAndVerify. accountType is NOT a token claim (it's the KYC-reviewer
  // signal, role stays 'buyer'), so a profile reload — not a token refresh — is
  // what advances the panel.
  return <DeclareIntent user={user} onDeclared={reload} />
}

/* ─────────────── Step 1 — flip the account to a supplier ─────────────── */

function DeclareIntent({
  user,
  onDeclared,
}: {
  user: UserClaims | null
  onDeclared: () => void
}) {
  const [company, setCompany] = useState('')
  const [contact, setContact] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Prefill from what we already know about them — a buyer who signed up as a
  // business has a company name; an individual may not, so we ask.
  useEffect(() => {
    if (!user) return
    if (user.companyName) setCompany((c) => c || user.companyName!)
    if (user.contactPerson) setContact((c) => c || user.contactPerson!)
  }, [user])

  async function start() {
    const contactName = contact.trim() || user?.contactPerson?.trim() || user?.email?.split('@')[0] || 'Account holder'
    const companyName = company.trim()
    if (!companyName) { setError('Enter your company name — buyers see who they’re dealing with.'); return }
    setBusy(true)
    setError(null)
    try {
      // Merge-safe: the wizard calls this same endpoint. Terms were accepted at
      // the original signup; re-affirming here is honest (they're agreeing to
      // the supplier terms) and the server just re-stamps the timestamp.
      await meApi.signupProfile({
        accountType: 'supplier',
        contactName,
        companyName,
        termsAccepted: true,
      })
      onDeclared()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Couldn’t start your supplier account — try again.')
    } finally {
      setBusy(false)
    }
  }

  const reduce = useReducedMotion()
  return (
    <div className="mx-auto max-w-4xl px-6 py-12 md:py-16" data-testid="become-supplier-declare">
      {/* ── Hero — frosted navy glass panel with the diamond motif ── */}
      <motion.div
        initial={reduce ? false : { opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="relative overflow-hidden rounded-[22px] border backdrop-blur-xl shadow-elev-3"
        style={{
          borderColor: 'rgba(201,169,74,0.28)',
          background:
            'linear-gradient(135deg, rgba(10,31,60,0.90) 0%, rgba(14,42,79,0.82) 46%, rgba(23,69,111,0.70) 100%)',
        }}
      >
        <div className="relative grid gap-6 p-8 md:grid-cols-[1fr_auto] md:items-center md:p-10">
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gold-300">Start selling</p>
            <h1 className="mt-3 font-serif text-3xl md:text-[2.6rem] leading-[1.08] tracking-tight text-white">
              List your stock on DiaEx
            </h1>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-white/70">
              You already have an account — no need to sign up again. Turn on selling, connect your
              inventory, and submit your verification documents. Your stock goes live once our team
              verifies you (1–2 business days).
            </p>
          </div>
          <div className="hidden h-40 w-40 shrink-0 md:block">
            <DiamondMotif animate={!reduce} />
          </div>
        </div>
        {/* gold hairline glow along the base */}
        <div
          className="absolute inset-x-0 bottom-0 h-px"
          style={{ background: 'linear-gradient(90deg, transparent, rgba(201,169,74,0.6), transparent)' }}
        />
      </motion.div>

      {/* ── The one form: flip the account to supplier ── */}
      <motion.div
        initial={reduce ? false : { opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, delay: 0.12, ease: [0.16, 1, 0.3, 1] }}
        className="mt-6 rounded-card border border-line bg-paper p-6 shadow-elev-2"
      >
        <div className="mb-5 flex items-center gap-2.5">
          <span
            className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-gold-600"
            style={{ background: 'rgba(201,169,74,0.14)', border: '1px solid rgba(201,169,74,0.4)' }}
            aria-hidden
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 21h18" /><path d="M5 21V7l7-4 7 4v14" /><path d="M9 21v-6h6v6" />
            </svg>
          </span>
          <h2 className="font-serif text-lg font-semibold text-ink">Your business</h2>
        </div>
        <label className="block text-sm font-medium text-ink" htmlFor="bs-company">Company name</label>
        <input
          id="bs-company"
          type="text"
          value={company}
          onChange={(e) => setCompany(e.target.value)}
          placeholder="Your registered business name"
          className="mt-1.5 w-full rounded-md border border-line bg-paper px-3 py-2.5 text-sm text-ink focus:border-gold-500 focus:outline-none transition-colors"
          data-testid="become-supplier-company"
        />
        <label className="mt-4 block text-sm font-medium text-ink" htmlFor="bs-contact">Contact name</label>
        <input
          id="bs-contact"
          type="text"
          value={contact}
          onChange={(e) => setContact(e.target.value)}
          placeholder="Who buyers reach"
          className="mt-1.5 w-full rounded-md border border-line bg-paper px-3 py-2.5 text-sm text-ink focus:border-gold-500 focus:outline-none transition-colors"
          data-testid="become-supplier-contact"
        />
        {error && <p className="mt-3 text-sm text-danger" role="alert">{error}</p>}
        <button
          type="button"
          onClick={() => void start()}
          disabled={busy}
          className="btn-primary mt-5 inline-flex disabled:opacity-50"
          data-testid="become-supplier-start"
        >
          {busy ? 'Setting up…' : 'Start selling'}
        </button>
        <p className="mt-3 text-xs text-soft">
          By continuing you agree to the supplier terms in our{' '}
          <a href="/terms" className="underline hover:text-ink">Terms</a>.
        </p>
      </motion.div>
    </div>
  )
}

/* ─────────────── Step 2 — connect inventory + submit docs ─────────────── */

function ConnectAndVerify() {
  const [kycStatus, setKycStatus] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    kycApi.status()
      .then((s) => { if (!cancelled) setKycStatus(s.status ?? null) })
      .catch(() => { if (!cancelled) setKycStatus(null) })
    return () => { cancelled = true }
  }, [])

  const approved = kycStatus === 'approved'
  const submitted = kycStatus === 'submitted'
  const reduce = useReducedMotion()

  return (
    <div className="mx-auto max-w-3xl px-6 py-12" data-testid="become-supplier-connect">
      {/* ── Hero — frosted navy glass panel with the diamond motif ── */}
      <motion.div
        initial={reduce ? false : { opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="relative overflow-hidden rounded-[22px] border backdrop-blur-xl shadow-elev-3"
        style={{
          borderColor: 'rgba(201,169,74,0.28)',
          background:
            'linear-gradient(135deg, rgba(10,31,60,0.90) 0%, rgba(14,42,79,0.82) 46%, rgba(23,69,111,0.70) 100%)',
        }}
      >
        <div className="relative flex items-center justify-between gap-6 p-8 md:p-10">
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gold-300">Start selling</p>
            <h1 className="mt-3 font-serif text-3xl md:text-[2.6rem] leading-[1.08] tracking-tight text-white">
              Get your stock live
            </h1>
            <p className="mt-3 max-w-md text-sm leading-relaxed text-white/70">
              Two steps: connect your inventory, then verify your business. We handle the rest.
            </p>
          </div>
          <div className="hidden h-32 w-32 shrink-0 sm:block">
            <DiamondMotif animate={!reduce} />
          </div>
        </div>
        <div
          className="absolute inset-x-0 bottom-0 h-px"
          style={{ background: 'linear-gradient(90deg, transparent, rgba(201,169,74,0.6), transparent)' }}
        />
      </motion.div>

      {/* The LIVE gate, stated out loud. Connecting inventory is deferrable, but
          nothing reaches buyers until the documents are submitted AND verified.
          Colored + frosted: green glass when verified, gold glass while pending. */}
      <motion.div
        initial={reduce ? false : { opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
        className="mt-6 flex items-start gap-3.5 rounded-card border p-4 text-sm text-ink backdrop-blur-sm"
        style={
          approved
            ? { borderColor: 'rgba(46,125,91,0.4)', background: 'rgba(46,125,91,0.08)' }
            : { borderColor: 'rgba(201,169,74,0.5)', background: 'rgba(201,169,74,0.10)' }
        }
        data-testid="become-supplier-live-gate"
        data-kyc-status={kycStatus ?? 'unknown'}
      >
        <span
          className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-full"
          style={
            approved
              ? { background: 'rgba(46,125,91,0.16)', color: 'var(--success)' }
              : { background: 'rgba(201,169,74,0.16)', color: 'var(--gold-600)' }
          }
          aria-hidden
        >
          {approved ? (
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 6 9 17l-5-5" />
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="9" /><path d="M12 8v4l2.5 2.5" />
            </svg>
          )}
        </span>
        <div className="min-w-0">
          {approved ? (
            <p><span className="font-semibold">You’re verified.</span> Once your inventory is connected and marked live, your stock shows to buyers.</p>
          ) : submitted ? (
            <p><span className="font-semibold">Documents received.</span> Our team is verifying you (1–2 business days). Your stock stays hidden until then — connect your inventory now so it’s ready to go live.</p>
          ) : (
            <p><span className="font-semibold">Submit your documents to start selling.</span> Your stock does not go live until we’ve verified your account. Connect your inventory below, then upload your verification documents.</p>
          )}
        </div>
      </motion.div>

      {/* 1 — connect inventory (deferrable via "Save for later" inside the card).
          Reuses the exact supplier My-Stock surface; no wizard. */}
      <StepSection n={1} title="Connect your inventory" reduce={!!reduce} delay={0.18}>
        <InventoryConnectCard />
      </StepSection>

      {/* 2 — verification documents. The gate on going live. */}
      <StepSection n={2} title="Verify your business" reduce={!!reduce} delay={0.26} last>
        <VerificationDocs />
      </StepSection>
    </div>
  )
}

/* A refined numbered progress spine: gold active node + a hairline rail that
 * connects the steps. Structural only — carries the same children, no behavior. */
function StepSection({
  n, title, children, reduce, delay, last = false,
}: {
  n: number
  title: string
  children: React.ReactNode
  reduce: boolean
  delay: number
  last?: boolean
}) {
  return (
    <motion.section
      initial={reduce ? false : { opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay, ease: [0.16, 1, 0.3, 1] }}
      className="relative mt-8 pl-11"
    >
      {/* the node */}
      <span
        className="absolute left-0 top-0 grid h-8 w-8 place-items-center rounded-full font-serif text-sm font-bold text-navy-900"
        style={{
          background: 'linear-gradient(135deg, #e4cd86, #c9a94a)',
          boxShadow: '0 2px 8px rgba(201,169,74,0.35), 0 0 0 4px rgba(201,169,74,0.10)',
        }}
        aria-hidden
      >
        {n}
      </span>
      {/* the rail down to the next node */}
      {!last && (
        <span
          className="absolute left-4 top-9 -ml-px w-px"
          style={{ bottom: '-2rem', background: 'linear-gradient(to bottom, rgba(201,169,74,0.5), rgba(201,169,74,0.08))' }}
          aria-hidden
        />
      )}
      <h2 className="mb-3 pt-1 text-[13px] font-semibold uppercase tracking-[0.1em] text-soft">{title}</h2>
      {children}
    </motion.section>
  )
}
